import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch } from '../../../app/redux/hooks';
import { appSubscriptionService } from '../../../common/subscriptions/appSubscriptionService';
import { TransactionLineItemAddRequest, useAddTransactionLineItemMutation } from '../../api';
import { useFeatureFlags } from '../../config';
import { showErrorDialog } from '../../error-dialog/errorDialogSlice';
import { setAddToTransactionInProgress, setTransaction, useTransaction } from '../orderSlice';

export default function OrderAddToTransaction() {
  const dispatch = useAppDispatch();
  const addItemList = useRef<TransactionLineItemAddRequest[]>([]);
  const [triggerAddLineItem] = useAddTransactionLineItemMutation();
  const [isAdding, setIsAdding] = useState(false);
  const transactionCurrent = useTransaction();
  const flags = useFeatureFlags();
  const useNewFlow = flags && flags.useWebsocketBasketProcess === true;

  const processNextItem = useCallback(async () => {
    console.warn('processNextItem called');
    if (useNewFlow) {
      if (addItemList.current.length > 0 && !isAdding) {
        setIsAdding(true);
        const transactionItem = addItemList.current.shift();
        if (transactionItem) {
          console.debug('Adding item to transaction', transactionItem);
          try {
            dispatch({
              type: 'signalr/add-item-to-basket',
              payload: {
                transactionId: transactionItem.transactionId,
                model: {
                  menuItemId: transactionItem.menuItemId,
                  unitOfSaleId: transactionItem.unitOfSaleId,
                  quantity: transactionItem.quantity,
                  condiments: transactionItem.condiments.map((x) => ({ menuItemId: x.menuItemId, unitOfSaleId: x.unitOfSaleId })),
                  transactionId: transactionItem.transactionId,
                  addOns: transactionItem.addOns.map((x) => ({ menuItemId: x.menuItemId })),
                  takeOffs: transactionItem.takeOffs.map((x) => ({ menuItemId: x.menuItemId })),
                  wasteReasonId: transactionItem.wasteReasonId,
                  operatorId: transactionItem.operatorId,
                  operatorLevel: transactionItem.operatorLevel,
                },
              },
            });
            // For websocket flow, we don't immediately process next item
            // The SignalR middleware will handle success/failure and we'll continue from there
          } catch (error) {
            console.error('Error adding item to transaction:', error);
            dispatch(
              showErrorDialog({
                message: 'Failed to add item to basket. Please try again.',
                dismissible: true,
              }),
            );
            // Item was already removed from queue, continue processing
            setIsAdding(false);
            if (addItemList.current.length > 0) processNextItem();
            else dispatch(setAddToTransactionInProgress(false));
          }
        }
      }
    } else {
      if (addItemList.current.length > 0 && !isAdding) {
        setIsAdding(true);
        const transactionItem = addItemList.current.shift();
        if (transactionItem) {
          console.debug('Adding item to transaction', transactionItem);
          try {
            await triggerAddLineItem({
              menuItemId: transactionItem.menuItemId,
              unitOfSaleId: transactionItem.unitOfSaleId,
              quantity: transactionItem.quantity,
              condiments: transactionItem.condiments.map((x) => ({ menuItemId: x.menuItemId, unitOfSaleId: x.unitOfSaleId })),
              transactionId: transactionItem.transactionId,
              addOns: transactionItem.addOns.map((x) => ({ menuItemId: x.menuItemId })),
              takeOffs: transactionItem.takeOffs.map((x) => ({ menuItemId: x.menuItemId })),
              wasteReasonId: transactionItem.wasteReasonId,
              operatorId: transactionItem.operatorId,
              operatorLevel: transactionItem.operatorLevel,
            })
              .unwrap()
              .then((txNew) => {
                // txNew is the whole transaction
                if (transactionCurrent.length === 1 && transactionCurrent[0].id === txNew.id) dispatch(setTransaction([txNew]));
              });
          } catch (error) {
            console.error('Error adding item to transaction:', error);
            dispatch(
              showErrorDialog({
                message: 'Failed to add item to basket. Please try again.',
                dismissible: true,
              }),
            );
            // Item was already removed from queue at line 57, continue processing
          } finally {
            setIsAdding(false);
            if (addItemList.current.length > 0) processNextItem();
            else dispatch(setAddToTransactionInProgress(false));
          }
        }
      }
    }
  }, [useNewFlow, isAdding, triggerAddLineItem, transactionCurrent, dispatch]);

  useEffect(() => {
    console.warn('isAdding or transactionCurrent changed, checking queue');
    if (useNewFlow) {
      // is there anythng in the queue?
      if (isAdding) {
        if (addItemList.current.length > 0) processNextItem();
        else {
          setIsAdding(false);
          dispatch(setAddToTransactionInProgress(false));
        }
      }
    }
  }, [dispatch, isAdding, processNextItem, transactionCurrent, useNewFlow]);

  useEffect(() => {
    const sub = appSubscriptionService.addToBasketObservable().subscribe((val) => {
      dispatch(setAddToTransactionInProgress(true));
      console.debug('Adding item to basket', val);
      addItemList.current.push(val);
      if (!isAdding) processNextItem();
    });
    return () => sub.unsubscribe();
  }, [dispatch, isAdding, processNextItem]);

  useEffect(() => {
    if (useNewFlow) {
      const sub = appSubscriptionService.addToBasketCompleteObservable().subscribe(() => {
        console.debug('Add to basket completed, continuing queue processing');
        setIsAdding(false);
        if (addItemList.current.length > 0) processNextItem();
        else dispatch(setAddToTransactionInProgress(false));
      });
      return () => sub.unsubscribe();
    }
  }, [dispatch, processNextItem, useNewFlow]);

  return <></>;
}
